import React, { useState } from "react";
import { useTranslation } from "../i18n";
import { m, AnimatePresence } from "framer-motion";
import { ChevronDown, MessageCircleQuestion } from "lucide-react";
import { clsx } from "clsx";
import { Helmet } from "react-helmet-async";

export default function FaqSection() {
  const { t } = useTranslation();
  const items = t("landing_faq.items", { returnObjects: true });
  const [openIndex, setOpenIndex] = useState(0); // Open first one by default

  const toggle = (i) => setOpenIndex((prev) => (prev === i ? null : i));

  return (
    <section
      id="faq"
      className="relative px-6 py-24 bg-gray-50 overflow-hidden"
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: Array.isArray(items)
              ? items.map((item) => ({
                  "@type": "Question",
                  name: item.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: item.answer,
                  },
                }))
              : [],
          })}
        </script>
      </Helmet>
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 rounded-full blur-[100px] opacity-40 mix-blend-multiply" />
      <div className="absolute top-20 -left-20 w-72 h-72 bg-blue-100 rounded-full blur-[80px] opacity-40 mix-blend-multiply" />

      <div className="max-w-3xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <m.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white shadow-md text-primary-600 mb-6"
          >
            <MessageCircleQuestion className="w-6 h-6" />
          </m.div>
          <m.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-extrabold text-gray-900 mb-4"
          >
            {t("landing_faq.title")}
          </m.h2>
          <m.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-gray-600"
          >
            {t("landing_faq.subtitle")}
          </m.p>
        </div>

        {/* Accordion List */}
        <div className="space-y-4">
          {Array.isArray(items) &&
            items.map((item, index) => (
              <FaqItem
                key={index}
                item={item}
                isOpen={openIndex === index}
                onClick={() => toggle(index)}
              />
            ))}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ item, isOpen, onClick }) {
  return (
    <m.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      className={clsx(
        "rounded-2xl border transition-all duration-300 overflow-hidden",
        isOpen
          ? "bg-white border-primary-100 shadow-lg shadow-primary-500/5 ring-1 ring-primary-500/10"
          : "bg-white/60 border-transparent hover:bg-white hover:border-gray-200"
      )}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
      >
        <span
          className={clsx(
            "text-lg font-bold pr-8 transition-colors",
            isOpen ? "text-primary-900" : "text-gray-900"
          )}
        >
          {item.question}
        </span>
        <div
          className={clsx(
            "w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300",
            isOpen
              ? "bg-primary-100 rotate-180"
              : "bg-gray-100 hover:bg-gray-200"
          )}
        >
          <ChevronDown
            className={clsx(
              "w-5 h-5 transition-colors",
              isOpen ? "text-primary-600" : "text-gray-500"
            )}
          />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-6 pb-6 text-gray-600 leading-relaxed text-base border-t border-gray-50/50 pt-2">
              {item.answer}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </m.div>
  );
}
